import { Link } from "react-router-dom";
import { LumaSection } from "./LumaSection";
import { AttendeeSignIn } from "./AttendeeSignIn";
import { WhySection } from "./WhySection";
import { SponsorSection } from "./SponsorSection";
import { Avatar } from "./Avatar";
import { NetworkingRoomPreview } from "./NetworkingRoomPreview";
import { UnknownEmailNotice } from "./UnknownEmailNotice";
import { useAttendees } from "../context/AttendeeContext";

function SignedInPanel() {
  const { me } = useAttendees();

  return (
    <section
      id="sign-in"
      className="flex h-full min-h-0 scroll-mt-6 flex-col rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 shadow-xl backdrop-blur md:p-5"
    >
      <div className="flex items-center gap-3">
        {me ? <Avatar attendee={me} size={44} /> : null}
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-400/90">
            You&apos;re signed in
          </p>
          <p className="mt-1 text-sm font-medium text-slate-100">
            {me?.firstName || me?.fullName || "Guest"}
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-slate-400">
        Browse attendee cards, faces, superpowers, and who to talk to in the room.
      </p>

      <Link
        to="/room"
        className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400"
      >
        See who&apos;s in the room
      </Link>

      <div className="mt-4">
        <NetworkingRoomPreview />
      </div>
    </section>
  );
}

function GuestListMissPanel({ email }: { email: string }) {
  return (
    <section
      id="sign-in"
      className="flex h-full min-h-0 scroll-mt-6 flex-col rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 shadow-xl backdrop-blur md:p-5"
    >
      <p className="text-xs font-medium uppercase tracking-wide text-amber-400/90">
        Signed in
      </p>
      <p className="mt-2 text-sm text-slate-200">{email}</p>
      <div className="mt-3">
        <UnknownEmailNotice compact />
      </div>
    </section>
  );
}

function SignInLoadingPanel() {
  return (
    <section className="flex h-full min-h-0 flex-col rounded-xl border border-slate-800 bg-slate-900/60 p-4 md:p-5 shadow-xl backdrop-blur">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Already registered?
      </p>
      <p className="mt-4 text-sm text-slate-400">Checking your sign-in…</p>
    </section>
  );
}

export const EmailGate = () => {
  const { me, bootstrapping, sessionEmail } = useAttendees();

  const signInColumn = bootstrapping ? (
    <SignInLoadingPanel />
  ) : me ? (
    <SignedInPanel />
  ) : sessionEmail ? (
    <GuestListMissPanel email={sessionEmail} />
  ) : (
    <AttendeeSignIn compact />
  );

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-2xl space-y-4">
        <header className="mx-auto max-w-md text-center">
          <img
            src="/logo.jpeg"
            alt="Pickles, Tacos, and Tech!"
            className="mx-auto block w-full max-w-sm"
          />
          <h1 className="mt-5 text-base font-medium text-slate-100">
            Pickles, Tacos, and Tech
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            High-signal networking for builders, founders &amp; makers.
          </p>
          {!me && !bootstrapping && !sessionEmail ? (
            <p className="mt-3 text-xs text-slate-500">
              Registered for an event?{" "}
              <a href="#sign-in" className="text-emerald-400 hover:text-emerald-300">
                Sign in
              </a>{" "}
              for day-of networking.
            </p>
          ) : null}
        </header>

        <div className="grid gap-4 md:grid-cols-[minmax(0,1.65fr)_minmax(220px,1fr)] md:items-stretch">
          <WhySection />
          {signInColumn}
        </div>
        <LumaSection />
        <SponsorSection />
      </div>
    </div>
  );
};
